import { Button, Grid2, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  OrganizationState,
  useOrganizationState,
} from 'features/organizations/state/useOrganizationState';
import RecurlyCardElement from 'components/RecurlyBillingInformation/RecurlyCardElement';
import { Controller, useForm } from 'react-hook-form';
import { generateClientToken } from 'shared/utils';
import { useRecurly } from '@recurly/react-recurly';
import { getCreditCardValidationErrors } from 'components/RecurlyBillingInformation/utils';
import { useOrganizationPurchase } from 'features/organizations/hooks/useOrganizationPurchase';
import { useRef, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import * as Sentry from '@sentry/capacitor';
import { useAuthState } from 'state/useAuthState';
import { OrganizationPurchaseSummary } from '../OrganizationPurchaseSummary';

interface OrganizationBillingData {
  companyName: string;
  postalCode: string;
  token?: string;
  acceptedTerms: boolean;
}

type OrganizationBillingInfoFormProps = {
  onSubmit: (values: Partial<OrganizationState>) => void;
};

export const OrganizationBillingInfoForm = (
  props: OrganizationBillingInfoFormProps,
): JSX.Element => {
  const { onSubmit } = props;
  const { t } = useTranslation();
  const recurly = useRecurly();
  const [errors, setErrors] = useState<string[]>([]);
  const { processOrganizationPurchase } = useOrganizationPurchase({});
  const orgState = useOrganizationState();
  const { user } = useAuthState();

  const { control, handleSubmit, formState, watch } =
    useForm<OrganizationBillingData>({
      defaultValues: {
        postalCode: '',
        acceptedTerms: false,
      },
    });

  const clientToken = useRef<string>(user ? '' : generateClientToken());

  const acceptedTerms = watch('acceptedTerms');

  const hasErrors = errors.length > 0;

  const submitToken = async (
    authorizationToken: string,
    values: OrganizationBillingData,
  ): Promise<void> => {
    await processOrganizationPurchase({
      authorizationToken,
      companyName: orgState.companyName,
      firstName: orgState.firstName,
      lastName: orgState.lastName,
      email: orgState.email,
      proSeats: orgState.proSeats,
      regularSeats: orgState.regularSeats,
      postalCode: values.postalCode,
      acceptedTerms: values.acceptedTerms,
      ...(clientToken.current ? { clientToken: clientToken.current } : {}),
    });

    onSubmit({
      ...orgState,
      ...(clientToken.current ? { clientToken: clientToken.current } : {}),
    });
  };

  const handleFormSubmit = async (
    values: OrganizationBillingData,
  ): Promise<void> => {
    try {
      const token = await new Promise<string>((resolve, reject) => {
        recurly.token(
          {
            first_name: orgState.firstName,
            last_name: orgState.lastName,
            postal_code: values.postalCode,
          },
          (err, recurlyToken) => {
            if (err) {
              const validationErrors = getCreditCardValidationErrors(err, t);
              setErrors(validationErrors);
              reject(err);
            } else {
              resolve(recurlyToken.id);
            }
          },
        );
      });

      await submitToken(token, values);
    } catch (error) {
      Sentry.captureException(error);
      enqueueSnackbar(t('common.unknownErrorTryAgain'), { variant: 'error' });
    }
  };

  return (
    <Grid2
      container
      direction="column"
      spacing={2}
      sx={{ flex: 1, pt: 1 }}
      component="form"
      noValidate
      onSubmit={handleSubmit(handleFormSubmit)}
    >
      <Grid2 container direction="column" spacing={2}>
        <Grid2>
          <Typography variant="h3">
            <b>{t('organizationPurchase.billingInfo')}</b>
          </Typography>
        </Grid2>
        <Grid2>
          <RecurlyCardElement error={hasErrors} />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            name="postalCode"
            control={control}
            render={({ field }) => (
              <TextField
                fullWidth
                label={t('organizationPurchase.zipcode.label')}
                required
                {...field}
              />
            )}
          />
        </Grid2>

        <Grid2>
          <Typography variant="h2">{t('organization.featureName')}</Typography>
        </Grid2>
        <Grid2 container justifyContent="space-between">
          <Typography component="span">
            {t('organizationPurchase.membership')}
          </Typography>
          <Typography component="span">{orgState.regularSeats}</Typography>
        </Grid2>
        <Grid2 container justifyContent="space-between">
          <Typography component="span">
            {t('organizationPurchase.pro')}
          </Typography>
          <Typography component="span">{orgState.proSeats}</Typography>
        </Grid2>

        <OrganizationPurchaseSummary
          regularSeats={orgState.regularSeats}
          proSeats={orgState.proSeats}
        />
      </Grid2>
      <Grid2
        size="grow"
        container
        direction="column"
        sx={{ justifyContent: 'flex-end' }}
      >
        <Grid2>
          <Controller
            name="acceptedTerms"
            control={control}
            render={({ field }) => (
              <label htmlFor="acceptedTerms">
                <input
                  id="acceptedTerms"
                  type="checkbox"
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  required
                  name={field.name}
                  ref={field.ref}
                />
                {t('organizationPurchase.acceptTerms')}
              </label>
            )}
          />
        </Grid2>
        <Grid2>
          <Button
            size="large"
            fullWidth
            type="submit"
            disabled={!acceptedTerms || formState.isSubmitting}
          >
            {t('organizationPurchase.purchase')}
          </Button>
        </Grid2>
      </Grid2>
    </Grid2>
  );
};
